import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Receipt, ShoppingBasket, Truck, FileText } from "lucide-react";
import api, { TRY, trDate, trWeekday } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Input } from "@/components/ui/input";

const CATS = { GIDA: "Gıda", PERSONEL: "Personel", LOJISTIK: "Lojistik", AMBALAJ: "Ambalaj", GENEL: "Genel", EKSTRA: "Ekstra Maliyet" };
const CAT_COLOR = { GIDA: "text-emerald-400 bg-emerald-400/10", PERSONEL: "text-sky-400 bg-sky-400/10", LOJISTIK: "text-amber-400 bg-amber-400/10", AMBALAJ: "text-purple-400 bg-purple-400/10", GENEL: "text-muted-foreground bg-muted", EKSTRA: "text-pink-400 bg-pink-400/10" };
const SRC_ICON = { daily_purchase: ShoppingBasket, supplier_ledger: Truck };
const shift = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };

export default function DailyExpenses({ goTo }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/expenses/daily", { params: { date } }).then((r) => setData(r.data)); }, [date]);

  const byCat = (data?.items || []).reduce((a, c) => ({ ...a, [c.category]: (a[c.category] || 0) + c.amount }), {});

  return (
    <Card className="p-5" data-testid="daily-expenses-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-rose-400" /> Günlük Harcama Kalemleri</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{trDate(date)} · {data?.count ?? 0} kalem</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Gün Toplamı</div><div className="font-mono text-xl font-bold text-rose-400" data-testid="daily-expenses-total">{TRY(data?.total)}</div></div>
          <div className="flex items-center gap-1">
            <button onClick={() => setDate(shift(date, -1))} data-testid="daily-expenses-prev" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronLeft className="h-4 w-4" /></button>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-expenses-date" className="h-9 w-40 font-mono" />
            <span className="text-xs text-muted-foreground font-medium" data-testid="daily-expenses-weekday">{trWeekday(date)}</span>
            <button onClick={() => setDate(shift(date, 1))} data-testid="daily-expenses-next" className="h-9 w-9 rounded-lg hover:bg-accent inline-flex items-center justify-center"><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
      {Object.keys(byCat).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {Object.entries(byCat).map(([k, v]) => (
            <span key={k} className={`rounded-full px-3 py-1 text-xs font-mono font-semibold ${CAT_COLOR[k] || CAT_COLOR.GENEL}`} data-testid={`daily-expenses-cat-${k}`}>{CATS[k] || k}: {TRY(v)}</span>
          ))}
        </div>
      )}
      {!data?.items?.length ? (
        <div className="py-8 text-center text-sm text-muted-foreground" data-testid="daily-expenses-empty">
          Bu tarihte harcama kaydı yok.
          {goTo && <button onClick={() => goTo("suppliers")} className="block mx-auto mt-2 text-primary underline" data-testid="daily-expenses-goto-suppliers">Günlük alım gir</button>}
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {data.items.map((c) => { const Icon = SRC_ICON[c.source] || FileText; return (
            <div key={c.id} className="flex items-start gap-3 py-2.5 text-sm" data-testid={`daily-expense-row-${c.id}`}>
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${CAT_COLOR[c.category] || CAT_COLOR.GENEL}`}><Icon className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{c.description || "—"}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                  <span>{CATS[c.category] || c.category}</span>
                  {c.supplier && <span>· {c.supplier}</span>}
                  {c.items?.length > 0 && <span>· {c.items.map((i) => `${i.name} x${i.qty}`).join(", ")}</span>}
                </div>
              </div>
              <div className="font-mono font-semibold whitespace-nowrap">{TRY(c.amount)}</div>
            </div>
          ); })}
        </div>
      )}
    </Card>
  );
}
